import config from '../config';

type GAClient = {
  initialize: (id: string) => void;
  send: (params: { hitType: string; page: string }) => void;
  event: (params: { category: string; action: string; label?: string }) => void;
};

const GA_MEASUREMENT_ID = config.gaMeasurementId;

let gaClientPromise: Promise<GAClient> | null = null;
let gaClient: GAClient | null = null;
let isGAInitialized = false;
let isGAInitializing = false;
const pendingPageViews: string[] = [];

const getAnalyticsConsent = () => localStorage.getItem('cookieConsent') === 'all';

const loadGAClient = async (): Promise<GAClient> => {
  if (gaClient) {
    return gaClient;
  }

  if (!gaClientPromise) {
    gaClientPromise = import('react-ga4').then(module => {
      const resolvedClient =
        (module as unknown as { default?: GAClient }).default ??
        (module as unknown as GAClient);

      gaClient = resolvedClient;
      return resolvedClient;
    });
  }

  return gaClientPromise;
};

const flushPendingPageViews = () => {
  if (!gaClient || !isGAInitialized || pendingPageViews.length === 0) {
    return;
  }

  const viewsToFlush = pendingPageViews.splice(0, pendingPageViews.length);
  viewsToFlush.forEach(page => {
    gaClient?.send({
      hitType: 'pageview',
      page,
    });
  });
};

export const initGA = async (): Promise<void> => {
  if (!getAnalyticsConsent() || isGAInitialized || isGAInitializing || !GA_MEASUREMENT_ID) {
    return;
  }

  isGAInitializing = true;

  try {
    const loadedGAClient = await loadGAClient();
    loadedGAClient.initialize(GA_MEASUREMENT_ID);
    gaClient = loadedGAClient;
    isGAInitialized = true;
    flushPendingPageViews();
  } finally {
    isGAInitializing = false;
  }
};

export const logPageView = (path?: string) => {
  const page = path || window.location.pathname;

  if (isGAInitialized && gaClient) {
    gaClient.send({
      hitType: 'pageview',
      page,
    });
    return;
  }

  if (getAnalyticsConsent()) {
    pendingPageViews.push(page);
  }
};

export const logEvent = (category: string, action: string, label?: string) => {
  if (isGAInitialized && gaClient) {
    gaClient.event({
      category,
      action,
      label,
    });
  }
};

/**
 * Check if event view tracking should be enabled based on cookie consent
 * Authenticated users: Always tracked (legitimate interest)
 * Anonymous users: Only with consent='all'
 */
export const shouldTrackEventView = (userId?: string): boolean => {
  // Track authenticated users regardless of consent (legitimate interest)
  // Track anonymous users only with consent
  return userId !== undefined || getAnalyticsConsent();
};

/**
 * Get or create session ID for anonymous tracking
 */
export const getAnalyticsSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `anon_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Parse UTM parameters from URL for enhanced tracking
 */
export const getUtmParameters = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};

  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  utmParams.forEach(param => {
    const value = params.get(param);
    if (value) {
      utm[param] = value;
    }
  });

  return utm;
};

